import { useEffect, useState, useRef } from "react";
import { STATUS } from "@/utils/enum";
import { runPricingEngine } from "@/services/pricingService";

const isPayloadValid = data => !Object.keys(data).some(key => data[key] === undefined);

// shipmentId and vin are used to track the last request params
export default function usePricingEngine({ shipmentData, onSuccess }) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const lastRequestParams = useRef(null);

  useEffect(() => {
    const isValid = isPayloadValid(shipmentData);

    if (!isValid) return undefined;

    if (lastRequestParams.current === `${shipmentData?.shipmentId}-${shipmentData?.vin}`) return undefined;

    const controller = new AbortController();

    const fetchPricingEngine = async () => {
      try {
        lastRequestParams.current = `${shipmentData?.shipmentId}-${shipmentData?.vin}`;
        setStatus(STATUS.LOADING);
        setError(null);
        setData(null);

        const response = await runPricingEngine(shipmentData, { signal: controller.signal });

        setData(response);
        setStatus(STATUS.SUCCESS);
        onSuccess(response);
      } catch (err) {
        // Request was cancelled, do nothing
        if (err.name === "AbortError") {
          console.error("Request was cancelled");
          return undefined;
        }

        setStatus(STATUS.ERROR);
        setError(err);
        console.error(err);
      }

      return undefined;
    };

    fetchPricingEngine();

    // cancel any pending request when the effect cleanup runs
    return () => {
      controller.abort();
    };
  }, [shipmentData, onSuccess]);

  return { data, status, error };
}
